import React, { useState } from "react";
import { Play } from "lucide-react";

// A YouTube embed that costs nothing until someone presses play.
//
// A bare <iframe> would pull roughly a megabyte of YouTube player JS into EVERY render of the
// landing page and the feature page — on the two pages where load time decides whether a visitor
// stays. So this renders a poster image and swaps in the real iframe only on click. The thumbnail
// comes straight from YouTube, so there is no asset to host or keep in sync with the video.
//
// youtube-nocookie.com is deliberate: the standard domain sets tracking cookies on page load, for
// every visitor, whether or not they ever watch. Here nothing is requested from YouTube until the
// click, and even then it is the privacy-preserving host — which also keeps the claim in our
// privacy policy ("analytics providers") honest, since a silent YouTube iframe is a third party we
// never disclosed.
export default function VideoEmbed({ id, title = "Video", className = "", poster = "maxresdefault" }) {
  // BOTH hooks must run before any early return — a conditional hook changes the hook order
  // between renders and React throws. `id` being absent is a real case (an unset constant while a
  // video is still being produced), so the guard below has to come after them, not before.
  const [playing, setPlaying] = useState(false);
  // `maxresdefault` is absent on some uploads; `hqdefault` always exists, so fall back on error
  // rather than showing a broken frame.
  const [thumb, setThumb] = useState(`https://i.ytimg.com/vi/${id}/${poster}.jpg`);

  if (!id) return null;

  return (
    <div className={`relative w-full overflow-hidden rounded-2xl border border-border bg-[#0F172A] ${className}`}
         style={{ aspectRatio: "16 / 9" }}>
      {playing ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          // autoplay is correct ONLY here: the user just clicked play, so it is the action they
          // asked for rather than something imposed on page load.
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ border: 0 }}
        />
      ) : (
        <button type="button" onClick={() => setPlaying(true)} aria-label={`Play: ${title}`}
                className="group absolute inset-0 w-full h-full cursor-pointer">
          <img src={thumb} alt="" loading="lazy" decoding="async"
               onError={() => setThumb(`https://i.ytimg.com/vi/${id}/hqdefault.jpg`)}
               className="absolute inset-0 w-full h-full object-cover opacity-90 transition-opacity group-hover:opacity-100" />
          <span className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
          <span className="absolute inset-0 grid place-items-center">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/95 shadow-xl transition-transform group-hover:scale-105">
              {/* translate-x nudges the glyph to the circle's optical centre — a triangle's visual
                  centre of mass sits left of its bounding box. */}
              <Play className="w-6 h-6 text-[#0F172A] translate-x-0.5" fill="currentColor" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
